import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { isPWAStandalone } from '../../lib/utils';
import { X, Brain, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNoshStore } from '../../lib/store';

const PHASE_INFO = {
  'connecting': {
    title: 'Preparing Image',
    icon: '📸',
    progress: 10
  },
  'optimizing': {
    title: 'Optimizing Image',
    icon: '✨',
    progress: 15
  },
  'uploading': {
    title: 'Uploading Securely',
    icon: '🔒',
    progress: 25
  },
  'processing': {
    title: 'Processing Request',
    icon: '⚡',
    progress: 35
  },
  'sending-request': {
    title: 'Sending to AI',
    icon: '🚀',
    progress: 45
  },
  'analyzing': {
    title: 'AI Analysis',
    icon: '🤖',
    progress: 70
  },
  'receiving-response': {
    title: 'Getting Results',
    icon: '📥',
    progress: 85
  },
  'parsing-response': {
    title: 'Processing Results',
    icon: '⚙️',
    progress: 95
  },
  'validating-data': {
    title: 'Final Validation',
    icon: '✅',
    progress: 98
  }
};

export function GlobalImageRecognitionModal() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isStandalone, setIsStandalone] = useState(false);
  
  const { 
    imageRecognition, 
    cancelImageRecognition,
    hideImageRecognitionModal
  } = useNoshStore();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check if app is running in standalone mode
    const checkStandalone = () => {
      setIsStandalone(isPWAStandalone());
    };

    handleResize();
    checkStandalone();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debug logging for mobile standalone mode - only log on modal open
  useEffect(() => {
    const isMobileStandalone = isMobile && isStandalone;
    if (isMobileStandalone && imageRecognition.showModal) {
      console.log('📱 [Global PWA Progress Modal] Opening:', {
        isMobile,
        isStandalone,
        isMobileStandalone,
        scanType: imageRecognition.scanType,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight
      });
    }
  }, [imageRecognition.showModal, imageRecognition.scanType, isMobile, isStandalone]); // Only track modal open/close

  if (!imageRecognition.showModal) return null;

  const phaseInfo = PHASE_INFO[imageRecognition.currentPhase as keyof typeof PHASE_INFO] || {
    title: 'Processing...',
    icon: '⏳',
    progress: 50
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = Math.min(phaseInfo.progress, 100);

  // Enhanced styling for mobile standalone mode
  const isMobileStandalone = isMobile && isStandalone;

  // Handle cancel with global state cleanup
  const handleCancel = () => {
    console.log('🚫 Global image recognition cancelled by user');
    cancelImageRecognition();
  };

  const stickyCard = (
    <AnimatePresence>
      {imageRecognition.showModal && (
        // Full-width container for perfect centering
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          className={`fixed top-0 left-0 right-0 ${
            isMobileStandalone ? 'z-[9999]' : 'z-50'
          }`}
          style={{
            width: '100vw',
            height: 'auto',
            pointerEvents: 'none' // Allow clicks to pass through the container
          }}
        >
          {/* Centered content container */}
          <div 
            className="flex justify-center items-start pt-4"
            style={{
              width: '100%',
              minHeight: 'auto'
            }}
          >
            {/* The actual modal card */}
            <div 
              className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden"
              style={{
                width: isMobile ? 'calc(100vw - 2rem)' : '420px',
                maxWidth: isMobile ? '380px' : '420px',
                pointerEvents: 'auto' // Re-enable clicks for the modal content
              }}
            >
              {/* Header Section */}
              <div className="flex items-center justify-between p-4 pb-3">
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white shadow-lg"
                  >
                    <Brain size={16} />
                  </motion.div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">
                      AI {imageRecognition.scanType === 'meal' ? 'Meal' : 'Food'} Recognition
                    </h3>
                  </div>
                </div>
                <button
                  onClick={handleCancel}
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={16} className="text-gray-500" />
                </button>
              </div>

              {/* Progress Section */}
              <div className="px-4 pb-4">
                {/* Current Phase */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">{phaseInfo.icon}</span>
                  <span className="text-sm font-medium text-gray-800">{phaseInfo.title}</span>
                  <div className="flex-1"></div>
                  <span className="text-xs text-gray-500">{formatTime(imageRecognition.elapsedTime)}</span>
                </div>

                {/* Progress Bar */}
                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercentage}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 rounded-full relative"
                    >
                      {/* Animated shine effect */}
                      <motion.div
                        animate={{ x: [-20, 320] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent rounded-full"
                        style={{ width: '20px' }}
                      />
                    </motion.div>
                  </div>
                  
                  {/* Progress percentage */}
                  <div className="flex justify-between items-center mt-2">
                    <div className="flex items-center gap-1">
                      <Zap size={12} className="text-blue-500" />
                      <span className="text-xs text-gray-600">
                        {imageRecognition.isActive ? 'Analyzing...' : 'Complete'}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-gray-700">{progressPercentage}%</span>
                  </div>
                </div>

                {/* Processing Animation */}
                <div className="flex items-center justify-center mt-3 gap-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      animate={{ 
                        scale: [1, 1.2, 1],
                        opacity: [0.7, 1, 0.7]
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        delay: i * 0.2,
                        ease: "easeInOut"
                      }}
                      className="w-1.5 h-1.5 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"
                    />
                  ))}
                </div>

                {/* Error Display */}
                {imageRecognition.error && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-600 font-medium">Error:</p>
                    <p className="text-xs text-red-500">{imageRecognition.error}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(stickyCard, document.body);
} 